import * as Accountant from 'app/calcs/Accountant'
import { IOPensionCalculations } from 'app/calcs/customers/IO'
import { makeIOComputer } from 'app/calcs/customers/IO/Factory';
import { IIOPensionInputs, IOFormOfPayment } from 'app/calcs/customers/IO/interfaces'
import { Local697FormOfPayment } from 'app/calcs/customers/local697/interfaces'
import { FormOfPaymentOptions } from 'app/calcs/pensions'
import { AllFormsOfPayment, IMonthlyPensionPayments, SimplePensionFormOfPayment } from 'app/calcs/pensions/interfaces'
import { MaterialInput, RadioGroup } from 'app/components'
import { RetirementAgeSelector } from 'app/components/form/RetirementAgeSelector'
import { Infobox } from 'app/components/Infobox';
import MaterialNumber from 'app/components/material/MaterialNumber';
import { FormOfPayment } from 'app/components/questions/FormOfPayment'
import { IProfileContainerState, PensionQuestionsGroupContainer } from 'app/containers/PipelineSection/Pension/Container';
import { IQuestionsGroupContainerProps, QuestionsGroupContainer } from 'app/containers/PipelineSection/QuestionGroupContainer'
import { dateAtAge, IRetirementPerson, isSamePersonForSSA, Profile } from 'app/redux/Profile/model'
import { theCensusInfo } from 'app/services/models/PublicCensusInfo'
import * as utils from 'app/utils'
import * as _ from 'lodash'
import * as moment from 'moment'
import * as React from 'react'
import { Button, ButtonGroup, Col, FormGroup, ListGroup, ListGroupItem, OverlayTrigger, Row, Tooltip } from 'react-bootstrap'

interface IIOPensionContainerState extends IProfileContainerState {
  headOfHousehold: IRetirementPerson
  monthlyPayout: string

  // these are synced with profile.pension.IO
  beginPaymentsAge: moment.Duration
  formOfPayment: IOFormOfPayment
  yearsOfAMembership: moment.Duration,
  participatesInIO: boolean
}

class IOPensionContainer extends PensionQuestionsGroupContainer<IQuestionsGroupContainerProps, IIOPensionContainerState> {
  beginPaymentsAgeNS = 'pension.IO.beginPaymentsAge'
  yearsOfAMembershipNS = 'pension.IO.yearsOfAMembership'
  formOfPaymentNS = 'pension.IO.formOfPayment'
  participatesInIONS = 'pension.IO.participatesInIO'

  state: IIOPensionContainerState = {
    profile: null,
    headOfHousehold: null,
    formOfPayment: this.getValueForQuestion(this.formOfPaymentNS) || SimplePensionFormOfPayment.singleLifeOnly,
    yearsOfAMembership: this.getValueForQuestion(this.yearsOfAMembershipNS) || null,
    monthlyPayout: '$0',
    beginPaymentsAge: this.getValueForQuestion(this.beginPaymentsAgeNS) || null,
    participatesInIO: this.getValueForQuestion(this.participatesInIONS) || false
  }

  initCalc = false

  debounceCalculations = _.debounce(() => {
    const person = this.state.headOfHousehold
    const profile = this.state.profile
    const inputs = this.state.profile.pension.IO
    const { beginPaymentsAge } = this.state;
    const when = dateAtAge(person, beginPaymentsAge)

    const computer = makeIOComputer(this.state.profile)
    computer.setSpouse(profile.getSpouse())
    computer.getMonthlyPensionAmount(person, inputs, when)
      .then((payout) => {
        const monthlyPayout = utils.toCurrency(payout.amount)
        this.setState({ monthlyPayout })
      })

  }, 300)

  componentWillMount() {
    this.onAppStateUpdate((appState) => {
      const profile = appState.Profile.getNative('info')
      const headOfHousehold = profile.getHeadOfHousehold()

      if (profile.pension.IO) {
        this.debounceCalculations();
      }

      this.setState({ headOfHousehold, profile })
    })
  }

  componentWillUpdate(nextProps: IQuestionsGroupContainerProps, nextState: IIOPensionContainerState) {
    this.updateCalculations(nextState)
  }

  updateCalculations(nextState: IIOPensionContainerState) {
    const { profile, formOfPayment, headOfHousehold, yearsOfAMembership, beginPaymentsAge, participatesInIO } = nextState

    if (!profile) { return }

    profile.pension = profile.pension || {}
    nextState.beginPaymentsAge = nextState.beginPaymentsAge || moment.duration(headOfHousehold.selectedRetirementAge, 'years')

    const updates = {
      beginPaymentsAge,
      formOfPayment,
      yearsOfAMembership,
      participatesInIO
    }
    const newIO = _.defaults({}, updates, profile.pension.IO)

    if (!this.initCalc || !_.isEqual(profile.pension.IO, newIO)) {
      this.initCalc = true
      profile.pension.IO = newIO
      this.debounceCalculations()
    }
  }

  updateParticipatesInIO(e: any) {
    const participatesInIO = !this.state.participatesInIO
    this.setState({ participatesInIO })

    this.markSignificantProfileUpdate(this.state.profile)
  }

  updatePaymentsAge(beginPaymentsAge: moment.Duration) {
    this.state.profile.pension.IO.beginPaymentsAge = beginPaymentsAge
    this.setState({ beginPaymentsAge })

    this.markSignificantProfileUpdate(this.state.profile)
  }

  updateYearsOfAMembership(yos: number) {
    const yearsOfAMembership = moment.duration(yos, 'years')
    this.state.profile.pension.IO.yearsOfAMembership = yearsOfAMembership
    this.setState({ yearsOfAMembership })

    this.markSignificantProfileUpdate(this.state.profile)
  }

  render() {

    if (!this.state.headOfHousehold) { return null }
    if (!this.state.profile.pension || !this.state.profile.pension.IO || !this.state.beginPaymentsAge) { return null }

    const censusDateText = theCensusInfo.censusDate.format('LL')

    const profile = this.state.profile
    const spouseIsValid = profile.spouseIsValid()

    const { yearsOfAMembership } = this.state.profile.pension.IO
    let yearsOfAMembershipNum = null
    let yearsOfAMembershipISO = ''
    if (moment.isDuration(yearsOfAMembership)) {
      yearsOfAMembershipNum = yearsOfAMembership.years()
      yearsOfAMembershipISO = yearsOfAMembership.toISOString()
    }

    const currentFormOfPayment = FormOfPaymentOptions
      .find((fopo) => fopo.value === this.state.formOfPayment)
      .label

    const beginPaymentsAgeText = utils.toNiceAge(this.state.beginPaymentsAge)
    const showSummary = (this.state.monthlyPayout !== '$0') && this.state.participatesInIO

    const findFop = (e: AllFormsOfPayment) => _.some(FormOfPaymentOptions, (q) => q.value === e && (spouseIsValid || !q.needsSpouse));
    const allowedFormsOfPayment = _.filter([
      IOFormOfPayment.lifeOnly,
      SimplePensionFormOfPayment.FiftyPercentJointAndSurvivor,
      // removed 10-11-2021 - 697 has its own class and this should not show up anywhere else
      //Local697FormOfPayment.hundredPercentHusbandAndWife
    ], findFop)

    const bStyle = {
      position: 'relative' as 'relative',
      top: '-2.9vw',
      left: '2vw',
      fontSize: '1.5vw',
    }

    return (
      <section className="animated fadeIn">
        <h1>IO / PBF Pension</h1>
        <h5 className="lead">Please input information related to the IBEW Pension Benefit Fund (aka IO or PBF).</h5>

        <Row>
          <Col md={6}>

            <FormGroup>
              <div className="material-checkbox-group hasValue">
                <label className="material-input-placeholder">Do you participate in the IO pension?</label>
                <input type="checkbox"
                  className="form-control material-input"
                  checked={this.state.participatesInIO}
                  onChange={(e) => this.updateParticipatesInIO(e)}
                  name={this.participatesInIONS} />
                <div style={bStyle}>
                  {this.state.participatesInIO ? 'yes, I do' : 'no, I don\'t'}
                </div>
              </div>
            </FormGroup>

            {this.state.participatesInIO &&
              <div>

                <br />
                <FormGroup>
                  <input type="hidden" name={this.yearsOfAMembershipNS}
                    accept="text/duration-years"
                    value={yearsOfAMembershipISO} />
                  <MaterialNumber
                    anyPlaceholder={
                      <span>How many years of IO service do you have?
                      <Infobox>
                          Note that IO benefits never vest.
                          Rather participation is based upon continuous
                          submission of dues to the Local. Failure to
                          maintain your dues submission results in the
                          forfeiture of all past credits.
                      </Infobox>
                      </span>
                    }
                    onChange={(e) => this.updateYearsOfAMembership(this.toNumber(e.currentTarget.value))}
                    value={yearsOfAMembershipNum} />
                </FormGroup>

                <FormGroup>
                  <RetirementAgeSelector min={62} max={70}
                    placeholder="What age will you begin payments?"
                    selectedValue={this.state.beginPaymentsAge} onChange={(a) => this.updatePaymentsAge(a)} />
                </FormGroup>

                <FormGroup>
                  <FormOfPayment name={this.formOfPaymentNS}
                    defaultValue={this.state.formOfPayment}
                    options={allowedFormsOfPayment}
                    onChange={(formOfPayment) => this.setStateAndSave({ formOfPayment })} />
                </FormGroup>
              </div>
            }

          </Col>
          <Col md={6}>
            {showSummary && <h2 className="animated fadeIn">
              If {this.state.headOfHousehold.firstName} begins to take payments at age&nbsp;
              <a>{beginPaymentsAgeText}</a>&nbsp;
              in the form of&nbsp;
              <a>{currentFormOfPayment}</a>&nbsp;
              the monthly amount will be <a>{this.state.monthlyPayout}</a>.&nbsp;
            </h2>}

          </Col>
        </Row>
      </section >
    )
  }
}

const IOPensionQuestionsGroup = () => (
  <IOPensionContainer
    header="IO / PBF Pension" icon="" />
)

export {
  IOPensionQuestionsGroup,
}



// WEBPACK FOOTER //
// ./src/app/containers/PipelineSection/pension/IO/index.tsx